import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Observable, of, Subject } from "rxjs";
import { UntypedFormControl } from "@angular/forms";
import { COMMA, ENTER, SPACE } from "@angular/cdk/keycodes";
import { MatChipInputEvent } from "@angular/material/chips";
import { catchError, map, startWith, takeUntil } from "rxjs/operators";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { Category } from '../../../../shared/interfaces/category';
import { Tag } from '../../../../shared/interfaces/tag';
import { Story } from '../../../../shared/interfaces/story';
import { ConsoleLoggerService } from '../../../../core/services/console-logger.service';
import { SlugifyPipe } from '../../../../shared/pipes/slugify.pipe';
import { nav_path } from '../../../../app-routing.module';
import { FirebaseError } from '@angular/fire/app/firebase';
import { FirestoreService } from '../../../../shared/services/firestore.service';
import { NewCategoryComponent } from '../../admin/admin-categories/new-category/new-category.component';
import { arrayRemove, arrayUnion } from '@angular/fire/firestore';

@Component({
  selector: 'anon-publish-dialog',
  templateUrl: './publish-dialog.component.html',
  styleUrls: ['./publish-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PublishDialogComponent implements OnInit, OnDestroy {
  public readonly nav_path = nav_path;
  private unsub$: Subject<any> = new Subject<any>();
  categories$: Observable<Category[] | any>;
  categoryControl = new UntypedFormControl();

  _allTags: string[] = [];
  allTags: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA, SPACE] as const;
  tagControl = new UntypedFormControl();
  autocompleteTags: Observable<string[]>;
  selectedStoryTags: string[] = [];
  @ViewChild('tagInput') tagInput!: ElementRef<HTMLInputElement>;


  constructor(
    public dialogRef: MatDialogRef<PublishDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {story: Story},
    private router: Router,
    private slugify: SlugifyPipe,
    private cLog: ConsoleLoggerService,
    private db: FirestoreService,
    private dialog: MatDialog,
  ) {
    this.categories$ = this.db.col$(`categories`)
      .pipe(catchError(error => {
        this.cLog.error(`Error getting categories`, error);
        return of([]);
      }));

    if (this.data.story.tags === undefined || this.data.story.tags === null) this.selectedStoryTags = [];
    else this.selectedStoryTags = Array.from(this.data.story.tags);
    this.db.col$(`tags`)
      .pipe(takeUntil(this.unsub$))
      .subscribe(
        tags => {
          this._allTags = [...(tags as Tag[]).map(tag => tag.slug)];
          this.allTags = this._allTags
            .filter((tagSlug) => !this.selectedStoryTags.includes(tagSlug))
        },
          error => this.cLog.error('Error getting tags', error)
      );
    this.autocompleteTags = this.tagControl.valueChanges
      .pipe(
        startWith(''),
        map((value: string | null) => (value ? this._filter(value) : this.allTags.slice())
        )
      );
  }
  ngOnInit() {
    this.categoryControl.setValue(this.data.story.categories);
    this.tagControl.setValue(this.data.story.tags);
  }

  async saveStory() {
    // todo: use a transaction here to update everything(story, categories, tags) or none of it if one fails
    const story = {...this.data.story};
    story.categories = this.categoryControl.value ?? null;
    story.tags = this.selectedStoryTags;
    const [removedCategories, addedCategories]: [string[], string[]] = this.handleCategories(this.data.story.categories!, story.categories!);
    const [removedTags, addedTags]: [string[], string[]] = this.handleTags(this.data.story.tags!, story.tags!);
    this.closeDialog();
    // create story is not already created
    if (story.hasOwnProperty('created')) {
      await this.db.update(`stories/${story.id}`, story);
      await this.addOrRemoveStoriesFromCategories(removedCategories, addedCategories, story.id!)
      await this.addOrRemoveStoriesFromTags(removedTags, addedTags, story.id!);
      this.router.navigate([nav_path.privateStories])
        .then(() => this.cLog.openSnackBar(`story updated: '${story.id}'`, `View`, {duration: 8000})
          .onAction().subscribe(() => this.router.navigate([nav_path.privateStories, story.id])));
    } else {
      if (await this.db.docExists(`stories/${story.slug}`)) {
        return this.cLog.error(`story with slug already exists: ${story.slug}`);
      }
      this.db.set(`stories/${story.slug}`, story)
        .then(async () => {
          await this.addOrRemoveStoriesFromCategories(removedCategories, addedCategories, story.slug!)
          await this.addOrRemoveStoriesFromTags(removedTags, addedTags, story.slug!);
          this.router.navigate([nav_path.privateStories])
            .then(() => this.cLog.openSnackBar(`story created: '${story.slug}'`, `View`, {duration: 8000})
              .onAction().subscribe(() => this.router.navigate([nav_path.privateStories, story.slug])));
        })
        .catch((error: FirebaseError) => this.cLog.error(`error creating story: '${story.slug}'`, error, story));
    }
  }

  async saveAsDraft() {
    this.data.story.status = 'draft';
    await this.saveStory();
  }

  async publishStory() {
    this.data.story.status = 'published';
    await this.saveStory();
  }

  closeDialog() { this.dialogRef.close(); }

  openCreateCategoryDialog() {
    this.dialog.open(NewCategoryComponent, {
      width: '800px',
      panelClass: 'new-category-dialog-container',
      id: 'new-category-dialog',
    });
  }

  /* Story Category Methods */
  /* Returns categories removed and added to the story, respectively */
  handleCategories(oldCategories: string[], newCategories: string[]): [string[], string[]] {
    if (!oldCategories) oldCategories = [];
    if (!newCategories) newCategories = [];

    let removedCategories: string[] = [];
    let addedCategories: string[] = [];
    oldCategories.forEach(oldCategory => {
      if (!newCategories.includes(oldCategory)) { removedCategories.push(oldCategory); }
    });
    newCategories.forEach(newCategory => {
      if (!oldCategories.includes(newCategory)) { addedCategories.push(newCategory); }
    });
    return [removedCategories, addedCategories];
  }

  async addOrRemoveStoriesFromCategories(categoriesToRemove: string[], categoriesToAdd: string[], story_id: string) {
    if (categoriesToRemove) categoriesToRemove
      .forEach(async category_slug => await this.db.update(`categories/${category_slug}`, { stories: arrayRemove(story_id) }));
    if (categoriesToAdd) categoriesToAdd
      .forEach(async category_slug => await this.db.update(`categories/${category_slug}`, { stories: arrayUnion(story_id) }));
    return;
  }

  /* Story Tag Methods */
  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (!value) return;
    if (this.selectedStoryTags.some(tag => {return tag === value})) return;

    // set tag object slug and name
    const tag = this.slugify.transform(value);
    // check if tag already exists
    const existingTagIndex = this.allTags.indexOf(tag);
    if (existingTagIndex >= 0) {
      this.selectedStoryTags.push(this.allTags[existingTagIndex]);
      this.allTags.splice(existingTagIndex, 1);
    }
    else this.selectedStoryTags.push(tag);

    event.chipInput!.clear();
    this.tagControl.setValue(null);
  }
  removeTag(tag: string): void {
    const index = this.selectedStoryTags.indexOf(tag);
    if (index >= 0) {
      this.selectedStoryTags.splice(index, 1);
      // if (tag.created) this.allTags.push(tag);
      this.allTags.push(tag);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const selectedTagIndex = this.allTags.indexOf(event.option.viewValue);
    if (selectedTagIndex >= 0) {
      this.selectedStoryTags.push(this.allTags[selectedTagIndex]);
      this.allTags.splice(selectedTagIndex, 1);
    }
    this.tagInput.nativeElement.value = '';
    this.tagControl.setValue(null);
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.allTags.filter(
      (tag) => tag?.toLowerCase().includes(filterValue)
    );
  }

  /* Returns the tags removed and added to the story, respectively */
  handleTags(oldTags: string[], newTags: string[]): [string[], string[]] {
    if (!oldTags) oldTags = [];
    if (!newTags) newTags = [];

    let removedTags: string[] = [];
    let addedTags: string[] = [];
    oldTags.forEach(oldTag => {
      if (newTags.indexOf(oldTag) < 0) removedTags.push(oldTag);
    })
    newTags.forEach((newTag) => {
      if (oldTags.indexOf(newTag) < 0) addedTags.push(newTag);
    })
    return [removedTags, addedTags];
  }
  async addOrRemoveStoriesFromTags(tagsToRemove: string[], tagsToAdd: string[], story_id: string) {
    if (tagsToRemove) tagsToRemove
      .forEach(async tagSlug => await this.db.update(`tags/${tagSlug}`, {stories: arrayRemove(story_id)}));
    if (tagsToAdd) tagsToAdd
      .forEach(async tagSlug => {
        if (this._allTags.includes(tagSlug)) {
          return await this.db.update(`tags/${tagSlug}`, {stories: arrayUnion(story_id)});
        }
        await this.db.set(`tags/${tagSlug}`, {slug: tagSlug, stories: arrayUnion(story_id)});
      });
    return;
  }


  ngOnDestroy() { this.unsub$.next(undefined); this.unsub$.complete(); }
}
